import React, { ReactNode } from 'react';
import styled from 'styled-components';

interface CarouselProps {
  children: ReactNode;
  height?: string;
}

const CustomCarousel: React.FC<CarouselProps> = ({ children, height = '400px' }) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [startX, setStartX] = React.useState(0);
  const [translateX, setTranslateX] = React.useState(0);
  const [lastMoveTime, setLastMoveTime] = React.useState(0);
  const carouselRef = React.useRef<HTMLDivElement>(null);
  const trackRef = React.useRef<HTMLDivElement>(null);
  const animationFrameRef = React.useRef<number>();
  const mousePosHistory = React.useRef<{ x: number; time: number }[]>([]);
  const VELOCITY_SAMPLE_WINDOW = 50;

  const currentIndexRef = React.useRef(0);
  const startingIndexRef = React.useRef(0);

  const DRAG_THRESHOLD = 5; // Minimum pixels to move to consider it a drag

  const handleMouseDown = (e: React.MouseEvent) => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Calculate actual current position when catching mid-animation
    if (trackRef.current && carouselRef.current) {
      const currentTranslate = trackRef.current.getBoundingClientRect().left - carouselRef.current.getBoundingClientRect().left;
      const currentIndex = Math.round((-currentTranslate / trackRef.current.scrollWidth) * React.Children.count(children));
      currentIndexRef.current = currentIndex;
      startingIndexRef.current = currentIndex;
    }

    setIsDragging(true);
    setStartX(e.clientX);
    setLastMoveTime(Date.now());
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    if (trackRef.current && carouselRef.current) {
      const currentTranslate = trackRef.current.getBoundingClientRect().left - carouselRef.current.getBoundingClientRect().left;
      const currentIndex = Math.round((-currentTranslate / trackRef.current.scrollWidth) * React.Children.count(children));
      currentIndexRef.current = currentIndex;
      startingIndexRef.current = currentIndex;
    }

    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setLastMoveTime(Date.now());
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();

    const x = e.touches[0].clientX;
    const walk = x - startX;

    mousePosHistory.current.push({ x: e.touches[0].clientX, time: Date.now() });

    if (trackRef.current) {
      const newTranslate = translateX + walk;
      setTranslateX(newTranslate);
      trackRef.current.style.transform = `translateX(${newTranslate}px)`;

      const trackWidth = trackRef.current.scrollWidth;
      const numItems = React.Children.count(children);
      const itemWidth = trackWidth / numItems;
      const newIndex = Math.round(-(translateX + walk) / itemWidth);
      currentIndexRef.current = newIndex;
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (!isDragging || !trackRef.current || !carouselRef.current) return;

    const lastTouch = e.changedTouches[0];
    const moveDistance = Math.abs(lastTouch.clientX - startX);
    if (moveDistance < DRAG_THRESHOLD) {
      // If the movement is less than the threshold, consider it a tap and do nothing
      return;
    }

    // Create a synthetic mouse event with the last touch position
    handleMouseUp({ clientX: lastTouch.clientX } as MouseEvent);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();

    const x = e.clientX;
    const walk = x - startX;

    mousePosHistory.current.push({ x: e.clientX, time: Date.now() });

    if (trackRef.current) {
      const newTranslate = translateX + walk;
      setTranslateX(newTranslate);
      trackRef.current.style.transform = `translateX(${newTranslate}px)`;

      const trackWidth = trackRef.current.scrollWidth;
      const numItems = React.Children.count(children);
      const itemWidth = trackWidth / numItems;
      const newIndex = Math.round(-(translateX + walk) / itemWidth);
      currentIndexRef.current = newIndex;
    }
  };

  const handleMouseUp = (e: MouseEvent) => {
    if (!isDragging || !trackRef.current || !carouselRef.current) return;
    setIsDragging(false);

    const moveDistance = Math.abs(e.clientX - startX);
    if (moveDistance < DRAG_THRESHOLD) {
      // If the movement is less than the threshold, consider it a click and do nothing
      return;
    }

    const trackWidth = trackRef.current.scrollWidth;
    const numItems = React.Children.count(children);
    const itemWidth = trackWidth / numItems;

    const now = Date.now();
    const recentPositions = mousePosHistory.current.filter((pos) => now - pos.time <= VELOCITY_SAMPLE_WINDOW);
    const velocity = recentPositions.length > 1 ? (e.clientX - recentPositions[0].x) / (now - recentPositions[0].time) : 0;
    mousePosHistory.current = [];

    // Add time threshold check
    const QUICK_DRAG_THRESHOLD = 100; // milliseconds
    const dragDuration = now - lastMoveTime;

    let targetIndex = currentIndexRef.current;
    const VELOCITY_THRESHOLD = 4.5; // px/ms

    if (Math.abs(velocity) > VELOCITY_THRESHOLD && !(dragDuration < QUICK_DRAG_THRESHOLD)) {
      const direction = velocity > 0 ? -1 : 1;
      const velocityFactor = Math.min(Math.abs(velocity) / VELOCITY_THRESHOLD, 3);
      const additionalSkips = Math.floor(velocityFactor);
      targetIndex = Math.max(0, Math.min(numItems - 1, currentIndexRef.current + direction * additionalSkips));

      const targetTranslate = -targetIndex * itemWidth;
      const startTranslate = translateX;
      const startTime = performance.now();
      const DURATION = 700;
      const OVERSHOOT = Math.min((Math.abs(velocity) / VELOCITY_THRESHOLD) * 100, 10);
      const RETURN_DURATION = 500;

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;

        // First phase: Overshoot with ease-out
        if (elapsed < DURATION) {
          const progress = elapsed / DURATION;
          const easeOut = 1 - (1 - progress) ** 3; // Cubic ease-out
          const overshootTarget = targetTranslate - direction * OVERSHOOT;
          const newTranslate = startTranslate + (overshootTarget - startTranslate) * easeOut;

          setTranslateX(newTranslate);
          trackRef.current!.style.transform = `translateX(${newTranslate}px)`;
          animationFrameRef.current = requestAnimationFrame(animate);
        }
        // Second phase: Return to target with ease-out
        else {
          const returnProgress = Math.min((elapsed - DURATION) / RETURN_DURATION, 1);
          const easeOut = 1 - (1 - returnProgress) ** 3; // Changed to cubic ease-out

          const overshootPosition = targetTranslate - direction * OVERSHOOT;
          const newTranslate = overshootPosition + (targetTranslate - overshootPosition) * easeOut;

          setTranslateX(newTranslate);
          trackRef.current!.style.transform = `translateX(${newTranslate}px)`;

          if (returnProgress < 1) {
            animationFrameRef.current = requestAnimationFrame(animate);
          } else {
            const finalSlideIndex = Math.abs(Math.round(targetTranslate / itemWidth));
            startingIndexRef.current = finalSlideIndex;
            currentIndexRef.current = finalSlideIndex;
          }
        }
      };

      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      const currentTranslate = trackRef.current!.getBoundingClientRect().left - carouselRef.current!.getBoundingClientRect().left;
      const currentIndex = Math.round(-currentTranslate / itemWidth);
      currentIndexRef.current = currentIndex;

      const direction = e.clientX > startX ? -1 : 1; // If mouse moved right -> -1, left -> 1

      if (startingIndexRef.current === currentIndex || dragDuration < QUICK_DRAG_THRESHOLD) {
        targetIndex = currentIndex + direction;
      }

      targetIndex = Math.max(0, Math.min(numItems - 1, targetIndex));
      const targetTranslate = -targetIndex * itemWidth;
      const startTime = performance.now();
      const DURATION = 800;
      const startTranslate = trackRef.current!.getBoundingClientRect().left - carouselRef.current!.getBoundingClientRect().left;

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / DURATION, 1);
        startingIndexRef.current = targetIndex;

        // Custom bezier curve: cubic-bezier(0.075, 0.82, 0.165, 1)
        const t = progress;
        // Using easeOutExpo for a smoother deceleration at the end
        const ease = t === 1 ? 1 : 1 - 2 ** (-10 * t);
        const newTranslate = startTranslate + (targetTranslate - startTranslate) * ease;

        setTranslateX(newTranslate);
        trackRef.current!.style.transform = `translateX(${newTranslate}px)`;

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animate);
        } else {
          const finalSlideIndex = Math.abs(Math.round(newTranslate / itemWidth));
          startingIndexRef.current = finalSlideIndex;
          currentIndexRef.current = finalSlideIndex;
        }
      };

      animationFrameRef.current = requestAnimationFrame(animate);
    }
  };

  React.useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => handleMouseMove(e);
    const handleGlobalMouseUp = (e: MouseEvent) => handleMouseUp(e);
    const handleGlobalTouchMove = (e: TouchEvent) => handleTouchMove(e);
    const handleGlobalTouchEnd = (e: TouchEvent) => handleTouchEnd(e);

    if (isDragging) {
      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
      window.addEventListener('touchmove', handleGlobalTouchMove);
      window.addEventListener('touchend', handleGlobalTouchEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchmove', handleGlobalTouchMove);
      window.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [isDragging, startX]);

  return (
    <CarouselWrapper ref={carouselRef} $height={height} onMouseDown={handleMouseDown} onTouchStart={handleTouchStart} $isDragging={isDragging}>
      <CarouselTrack ref={trackRef} $isDragging={isDragging}>
        {children}
      </CarouselTrack>
    </CarouselWrapper>
  );
};

export default CustomCarousel;

const CarouselWrapper = styled.div<{ $height: string; $isDragging: boolean }>`
  width: 100%;
  height: ${(props) => props.$height};
  overflow: visible;
  cursor: ${(props) => (props.$isDragging ? 'grabbing' : 'grab')};
  user-select: none;

  img {
    pointer-events: none;
    user-drag: none;
    -webkit-user-drag: none;
    user-select: none;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
  }
`;

const CarouselTrack = styled.div<{ $isDragging: boolean }>`
  display: flex;
  height: 100%;
  will-change: transform;

  > * {
    flex-shrink: 0;
    width: 100%;
  }
`;
